/**
 * FikoDokumGirisi.ts
 * SPH simülasyonu için döküm giriş elemanı (casting inlet)
 * Her giriş bağımsız bir parçacık kaynağıdır ve 25+ parametre ile kontrol edilir
 */

import {
  DokumGirisiConfig,
  AkisProfili,
  KesitSekli,
  TurbulansProfili,
  Vec3,
  Renk,
  vec3,
  renk,
  varsayilanDokumGirisi
} from '../FikoSPHParametreler';

import {
  ISPHEleman,
  ElemanTipi,
  olusturId
} from './ISPHEleman';

/**
 * Döküm giriş elemanı sınıfı
 * Sıvı simülasyonu için parçacık kaynağı olarak çalışır
 */
export class FikoDokumGirisi implements ISPHEleman {
  readonly tip = ElemanTipi.DOKUM_GIRISI;
  readonly id: string;

  // Pozisyon ve yön
  pozisyon: Vec3;
  yon: Vec3;

  // Akış parametreleri
  hiz: number;
  debi: number;
  yaricap: number;

  // Kesit geometrisi
  kesitSekli: KesitSekli;
  kesitBoyut: { w: number; h: number };

  // Parçacık ömrü
  maxMesafe: number;
  maxOmur: number;

  // Durum
  aktif: boolean;

  // Malzeme özellikleri
  malzemeId: string;
  sicaklik: number;

  // Türbülans
  turbulansProfili: TurbulansProfili;
  turbulansYogunluk: number;

  // Akış profili ve zamanlama
  akisProfili: AkisProfili;
  akisBaslangic: number;
  akisBitis: number;
  rampaYukselis: number;
  pulsePeriyot: number;
  pulseDuty: number;

  // Görselleştirme
  ad: string;
  renk: Renk;
  gorunur: boolean;

  constructor(config: Partial<DokumGirisiConfig> = {}) {
    this.id = olusturId();

    const varsayilan = varsayilanDokumGirisi();

    this.pozisyon = config.pozisyon || varsayilan.pozisyon;
    this.yon = config.yon || varsayilan.yon;
    this.hiz = config.hiz ?? varsayilan.hiz;
    this.debi = config.debi ?? varsayilan.debi;
    this.yaricap = config.yaricap ?? varsayilan.yaricap;
    this.kesitSekli = config.kesitSekli ?? varsayilan.kesitSekli;
    this.kesitBoyut = config.kesitBoyut || varsayilan.kesitBoyut;
    this.maxMesafe = config.maxMesafe ?? varsayilan.maxMesafe;
    this.maxOmur = config.maxOmur ?? varsayilan.maxOmur;
    this.aktif = config.aktif ?? varsayilan.aktif;
    this.malzemeId = config.malzemeId ?? varsayilan.malzemeId;
    this.sicaklik = config.sicaklik ?? varsayilan.sicaklik;
    this.turbulansProfili = config.turbulansProfili ?? varsayilan.turbulansProfili;
    this.turbulansYogunluk = config.turbulansYogunluk ?? varsayilan.turbulansYogunluk;
    this.akisProfili = config.akisProfili ?? varsayilan.akisProfili;
    this.akisBaslangic = config.akisBaslangic ?? varsayilan.akisBaslangic;
    this.akisBitis = config.akisBitis ?? varsayilan.akisBitis;
    this.rampaYukselis = config.rampaYukselis ?? varsayilan.rampaYukselis;
    this.pulsePeriyot = config.pulsePeriyot ?? varsayilan.pulsePeriyot;
    this.pulseDuty = config.pulseDuty ?? varsayilan.pulseDuty;
    this.ad = config.ad || varsayilan.ad;
    this.renk = config.renk || varsayilan.renk;
    this.gorunur = config.gorunur ?? varsayilan.gorunur;
  }

  /**
   * GPU için 128-byte struct oluştur
   * WGSL DokumGirisi struct'ı ile eşleşir
   */
  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(128);
    const view = new DataView(buffer);
    const floatView = new Float32Array(buffer);
    const uintView = new Uint32Array(buffer);

    let offset = 0;

    // [0-15] pozX, pozY, pozZ, yaricap
    floatView[0] = this.pozisyon.x;
    floatView[1] = this.pozisyon.y;
    floatView[2] = this.pozisyon.z;
    floatView[3] = this.yaricap;
    offset += 16;

    // [16-31] yonX, yonY, yonZ, hiz
    floatView[4] = this.yon.x;
    floatView[5] = this.yon.y;
    floatView[6] = this.yon.z;
    floatView[7] = this.hiz;
    offset += 16;

    // [32-47] debi, maxMesafe, maxOmur, aktif
    floatView[8] = this.debi;
    floatView[9] = this.maxMesafe;
    floatView[10] = this.maxOmur;
    uintView[11] = this.aktif ? 1 : 0;
    offset += 16;

    // [48-63] malzemeId, kesitTip, kesitW, kesitH
    // Parse malzemeId string to number (or use hash)
    const malzemeIdNum = parseInt(this.malzemeId) || 0;
    uintView[12] = malzemeIdNum;
    // Map KesitSekli enum to numeric
    const kesitSekilNum = this.kesitSekli === KesitSekli.DAIRE ? 0 : this.kesitSekli === KesitSekli.DIKDORTGEN ? 1 : this.kesitSekli === KesitSekli.SLOT ? 2 : 3;
    uintView[13] = kesitSekilNum;
    floatView[14] = this.kesitBoyut.w;
    floatView[15] = this.kesitBoyut.h;
    offset += 16;

    // [64-79] sicaklik, turbulans, akisProfil, zaman (şu anki zamanı 0 olarak başlat)
    floatView[16] = this.sicaklik;
    // Map TurbulansProfili enum and calculate turbulans value
    const turbulansValue = this.turbulansProfili === TurbulansProfili.LAMINAR ? 0.0 : this.turbulansYogunluk;
    floatView[17] = turbulansValue;
    // Map AkisProfili enum to numeric
    const akisProfilNum = this.akisProfili === AkisProfili.SABIT ? 0 : this.akisProfili === AkisProfili.RAMPA ? 1 : this.akisProfili === AkisProfili.SINUS ? 2 : this.akisProfili === AkisProfili.PULSE ? 3 : 4;
    uintView[18] = akisProfilNum;
    floatView[19] = 0.0; // zaman - simülasyon tarafından güncellenecek
    offset += 16;

    // [80-95] rampaYukselis, pulsePer, pulseDuty, renk (packed RGBA)
    floatView[20] = this.rampaYukselis;
    floatView[21] = this.pulsePeriyot;
    floatView[22] = this.pulseDuty;

    // Rengi packed uint32 olarak kaydet (RGBA8)
    const r = Math.floor(this.renk.r * 255);
    const g = Math.floor(this.renk.g * 255);
    const b = Math.floor(this.renk.b * 255);
    const a = Math.floor(this.renk.a * 255);
    uintView[23] = (r << 24) | (g << 16) | (b << 8) | a;
    offset += 16;

    // [96-111] akisBaslangic, akisBitis, pad, pad
    floatView[24] = this.akisBaslangic;
    floatView[25] = this.akisBitis;
    floatView[26] = 0.0; // padding
    floatView[27] = 0.0; // padding
    offset += 16;

    // [112-127] reserved (gelecek özellikler için)
    floatView[28] = 0.0;
    floatView[29] = 0.0;
    floatView[30] = 0.0;
    floatView[31] = 0.0;

    return buffer;
  }

  /**
   * GPU struct boyutu
   */
  gpuStructBoyut(): number {
    return 128;
  }

  /**
   * Anlık debi hesapla
   * Akış profili, zaman aralığı, rampa, sinüs ve pulse parametrelerine göre
   */
  hesaplaAnlikDebi(zaman: number): number {
    // Zaman aralığı kontrolü
    if (zaman < this.akisBaslangic || zaman > this.akisBitis) {
      return 0.0;
    }

    // Aktif değilse sıfır
    if (!this.aktif) {
      return 0.0;
    }

    let debiCarpan = 1.0;
    const goreceli_zaman = zaman - this.akisBaslangic;

    // Akış profiline göre
    switch (this.akisProfili) {
      case AkisProfili.SABIT:
        debiCarpan = 1.0;
        break;

      case AkisProfili.RAMPA:
        if (goreceli_zaman < this.rampaYukselis) {
          debiCarpan = goreceli_zaman / this.rampaYukselis;
        } else {
          debiCarpan = 1.0;
        }
        break;

      case AkisProfili.SINUS:
        // Sinüzoidal dalgalanma
        debiCarpan = 0.5 + 0.5 * Math.sin(2.0 * Math.PI * goreceli_zaman / this.pulsePeriyot);
        break;

      case AkisProfili.PULSE:
        // PWM tarzı pulse
        const fazOran = (goreceli_zaman % this.pulsePeriyot) / this.pulsePeriyot;
        debiCarpan = fazOran < this.pulseDuty ? 1.0 : 0.0;
        break;

      case AkisProfili.OZEL:
        // OZEL profil için default davranış (sabit)
        debiCarpan = 1.0;
        break;

      default:
        debiCarpan = 1.0;
    }

    return this.debi * debiCarpan;
  }

  /**
   * JSON serileştirme
   */
  toJSON(): any {
    return {
      tip: this.tip,
      id: this.id,
      pozisyon: this.pozisyon,
      yon: this.yon,
      hiz: this.hiz,
      debi: this.debi,
      yaricap: this.yaricap,
      kesitSekli: this.kesitSekli,
      kesitBoyut: this.kesitBoyut,
      maxMesafe: this.maxMesafe,
      maxOmur: this.maxOmur,
      aktif: this.aktif,
      malzemeId: this.malzemeId,
      sicaklik: this.sicaklik,
      turbulansProfili: this.turbulansProfili,
      turbulansYogunluk: this.turbulansYogunluk,
      akisProfili: this.akisProfili,
      akisBaslangic: this.akisBaslangic,
      akisBitis: this.akisBitis,
      rampaYukselis: this.rampaYukselis,
      pulsePeriyot: this.pulsePeriyot,
      pulseDuty: this.pulseDuty,
      ad: this.ad,
      renk: this.renk,
      gorunur: this.gorunur
    };
  }

  /**
   * JSON'dan yükle
   */
  static fromJSON(data: any): FikoDokumGirisi {
    const giris = new FikoDokumGirisi(data);
    if (data.id) {
      (giris as any).id = data.id; // ID'yi koru
    }
    return giris;
  }

  /**
   * Parametreleri doğrula
   * @returns Hata mesajları dizisi (boşsa geçerli)
   */
  dogrula(): string[] {
    const hatalar: string[] = [];

    if (this.yaricap <= 0) {
      hatalar.push('Yarıçap sıfırdan büyük olmalıdır');
    }

    if (this.debi < 0) {
      hatalar.push('Debi negatif olamaz');
    }

    if (this.hiz < 0) {
      hatalar.push('Hız negatif olamaz');
    }

    if (this.maxOmur <= 0) {
      hatalar.push('Maksimum ömür sıfırdan büyük olmalıdır');
    }

    if (this.akisBaslangic < 0) {
      hatalar.push('Akış başlangıç zamanı negatif olamaz');
    }

    if (this.akisBitis <= this.akisBaslangic) {
      hatalar.push('Akış bitiş zamanı başlangıçtan büyük olmalıdır');
    }

    if (this.rampaYukselis < 0) {
      hatalar.push('Rampa yükseliş süresi negatif olamaz');
    }

    if (this.pulsePeriyot <= 0) {
      hatalar.push('Pulse periyodu sıfırdan büyük olmalıdır');
    }

    if (this.pulseDuty < 0 || this.pulseDuty > 1) {
      hatalar.push('Pulse duty cycle 0-1 arasında olmalıdır');
    }

    if (this.sicaklik < 0) {
      hatalar.push('Sıcaklık negatif olamaz');
    }

    if (this.turbulansYogunluk < 0 || this.turbulansYogunluk > 1) {
      hatalar.push('Türbülans yoğunluğu 0-1 arasında olmalıdır');
    }

    // Yön vektörünün normalize olup olmadığını kontrol et
    const yonBoyut = Math.sqrt(
      this.yon.x * this.yon.x +
      this.yon.y * this.yon.y +
      this.yon.z * this.yon.z
    );
    if (Math.abs(yonBoyut - 1.0) > 0.01) {
      hatalar.push('Yön vektörü normalize edilmeli (boyut ≈ 1.0)');
    }

    return hatalar;
  }

  /**
   * Derin kopyalama
   */
  klonla(): FikoDokumGirisi {
    return new FikoDokumGirisi({
      pozisyon: { ...this.pozisyon },
      yon: { ...this.yon },
      hiz: this.hiz,
      debi: this.debi,
      yaricap: this.yaricap,
      kesitSekli: this.kesitSekli,
      kesitBoyut: { ...this.kesitBoyut },
      maxMesafe: this.maxMesafe,
      maxOmur: this.maxOmur,
      aktif: this.aktif,
      malzemeId: this.malzemeId,
      sicaklik: this.sicaklik,
      turbulansProfili: this.turbulansProfili,
      turbulansYogunluk: this.turbulansYogunluk,
      akisProfili: this.akisProfili,
      akisBaslangic: this.akisBaslangic,
      akisBitis: this.akisBitis,
      rampaYukselis: this.rampaYukselis,
      pulsePeriyot: this.pulsePeriyot,
      pulseDuty: this.pulseDuty,
      ad: this.ad,
      renk: { ...this.renk },
      gorunur: this.gorunur
    });
  }

  /**
   * Tüm parametre isimlerini listele
   */
  parametreListesi(): string[] {
    return [
      'pozisyon',
      'yon',
      'hiz',
      'debi',
      'yaricap',
      'kesitSekli',
      'kesitBoyut',
      'maxMesafe',
      'maxOmur',
      'aktif',
      'malzemeId',
      'sicaklik',
      'turbulansProfili',
      'turbulansYogunluk',
      'akisProfili',
      'akisBaslangic',
      'akisBitis',
      'rampaYukselis',
      'pulsePeriyot',
      'pulseDuty',
      'ad',
      'renk',
      'gorunur'
    ];
  }

  /**
   * İsme göre parametre ayarla
   */
  parametreAyarla(ad: string, deger: any): void {
    switch (ad) {
      case 'pozisyon':
        this.pozisyon = deger as Vec3;
        break;
      case 'yon':
        this.yon = deger as Vec3;
        break;
      case 'hiz':
        this.hiz = deger as number;
        break;
      case 'debi':
        this.debi = deger as number;
        break;
      case 'yaricap':
        this.yaricap = deger as number;
        break;
      case 'kesitSekli':
        this.kesitSekli = deger as KesitSekli;
        break;
      case 'kesitBoyut':
        this.kesitBoyut = deger as { w: number; h: number };
        break;
      case 'maxMesafe':
        this.maxMesafe = deger as number;
        break;
      case 'maxOmur':
        this.maxOmur = deger as number;
        break;
      case 'aktif':
        this.aktif = deger as boolean;
        break;
      case 'malzemeId':
        this.malzemeId = deger as string;
        break;
      case 'sicaklik':
        this.sicaklik = deger as number;
        break;
      case 'turbulansProfili':
        this.turbulansProfili = deger as TurbulansProfili;
        break;
      case 'turbulansYogunluk':
        this.turbulansYogunluk = deger as number;
        break;
      case 'akisProfili':
        this.akisProfili = deger as AkisProfili;
        break;
      case 'akisBaslangic':
        this.akisBaslangic = deger as number;
        break;
      case 'akisBitis':
        this.akisBitis = deger as number;
        break;
      case 'rampaYukselis':
        this.rampaYukselis = deger as number;
        break;
      case 'pulsePeriyot':
        this.pulsePeriyot = deger as number;
        break;
      case 'pulseDuty':
        this.pulseDuty = deger as number;
        break;
      case 'ad':
        this.ad = deger as string;
        break;
      case 'renk':
        this.renk = deger as Renk;
        break;
      case 'gorunur':
        this.gorunur = deger as boolean;
        break;
      default:
        console.warn(`Bilinmeyen parametre: ${ad}`);
    }
  }

  /**
   * İsme göre parametre al
   */
  parametreAl(ad: string): any {
    return (this as any)[ad];
  }
}
